import {
  AccountVerificationPendingError,
  type AccountCanonicalRefusal,
  type AccountRuntimePort,
  type AccountRuntimeResult,
} from '../foundation/account-port';
import { IdentityHttpClient, IdentityHttpError } from './identity-http';
import { canonicalRefusal } from './validated-refusal';

/** A validated 4xx envelope is the account owner's refusal; anything else stays a rejection. */
const refusal = (error: unknown): AccountCanonicalRefusal => canonicalRefusal(error, { requireClientStatus: true });

export interface AccountAdapterLifecycle {
  readonly isCurrent?: () => boolean;
  readonly beforeRequest?: () => void | Promise<void>;
  /** A dispatch stop only; it must not discard an already-issued receipt. */
  readonly canDispatch?: () => boolean;
}

/** Unwrap only generated success envelopes; never turn a failed write into success. */
export function createAccountRuntimePort(
  client: IdentityHttpClient,
  onSessionInvalidated?: () => void,
  lifecycle: AccountAdapterLifecycle = {},
): AccountRuntimePort {
  const assertCurrent = () => {
    // This is a local lifecycle fence, not a server denial or a receipt.
    if (lifecycle.isCurrent?.() === false) throw new IdentityHttpError('permission');
  };
  const invoke = async <Value>(
    request: () => Promise<Value>,
    invalidatesSession?: (value: Value) => boolean,
  ): Promise<AccountRuntimeResult<Value>> => {
    assertCurrent();
    let value: Value;
    try {
      if (lifecycle.beforeRequest) await lifecycle.beforeRequest();
      assertCurrent();
      if (lifecycle.canDispatch?.() === false) throw new AccountVerificationPendingError();
      value = await request();
    } catch (error) {
      assertCurrent();
      return refusal(error);
    }
    assertCurrent();
    // Invalidation may advance the runtime's epoch. Check before that owned
    // transition so its own successful acknowledgement is not marked stale.
    if (invalidatesSession?.(value)) onSessionInvalidated?.();
    return { kind: 'success', canonical: value };
  };
  return {
    readProfile: (command) => invoke(async () => (await client.read(command)).result.result),
    updateProfile: (command) => invoke(async () => (await client.mutate(command)).result),
    updateCredential: (command) => invoke(async () => (await client.mutate(command)).result),
    generateSecondFactor: (command) => invoke(async () => (await client.mutate(command)).result),
    enableSecondFactor: (command) => invoke(async () => (await client.mutate(command)).result),
    disableSecondFactor: (command) => invoke(async () => (await client.mutate(command)).result),
    deleteSecondFactorSecret: (command) => invoke(async () => (await client.mutate(command)).result),
    readSessions: (command) => invoke(async () => (await client.read(command)).result),
    revokeSession: (command) => invoke(async () => (await client.mutate(command)).result, (result) => result.current_session_revoked),
    revokeAllSessions: (command) => invoke(async () => (await client.mutate(command)).result, (result) => result.current_session_revoked),
  };
}
